import { createContext, ReactNode, useMemo, useReducer } from 'react';
import {
  MetaData,
  ModalAction,
  ModalActionType,
  ModalContextState,
  ModalName,
  ModalState
} from './types';

const initialState = {} as ModalState;

const modalReducer = (state = initialState, action: ModalAction) => {
  switch (action.type) {
    case ModalActionType.open:
      return {
        ...state,
        [action.payload.modalName]: {
          ...state[action.payload.modalName],
          isOpen: true,
          meta: action.payload.meta
        }
      };
    case ModalActionType.close:
      return {
        ...state,
        [action.payload.modalName]: {
          ...state[action.payload.modalName],
          isOpen: false,
          meta: undefined
        }
      };
    default:
      return state;
  }
};

export const ModalContext = createContext<ModalContextState | undefined>(
  undefined
);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, dispatch] = useReducer(modalReducer, initialState);

  const open = (modalName: ModalName, meta?: MetaData) => {
    dispatch({ type: ModalActionType.open, payload: { modalName, meta } });
  };

  const close = (modalName: ModalName) => {
    dispatch({ type: ModalActionType.close, payload: { modalName } });
  };

  const isModalOpen = useMemo(
    () => (modalName: ModalName) => !!modalState[modalName]?.isOpen,
    [modalState]
  );

  const contextValue = useMemo(
    () => ({
      state: modalState,
      open,
      close,
      isModalOpen
    }),
    [modalState, isModalOpen]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};
